import { useState, useEffect } from "react";
import PayrollCompleteness from "@/components/PayrollCompleteness";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Wallet, ArrowUpRight, ArrowDownRight, AlertCircle, CheckCircle2,
  Clock, TrendingUp, TrendingDown, ChevronRight, Users, CreditCard,
  Bell, BarChart3, Target, AlertTriangle, RefreshCw, Calendar,
  ArrowRight, PieChart, Circle, Landmark, FileText, Settings as SettingsIcon
} from "lucide-react";
import { apiFetch, API } from "@/utils/api";
import { getCompanyProfile } from "@/utils/companyProfile";
import RiskGauge from "@/components/RiskGauge";
import TensionCards from "@/components/TensionCards";
import ActionCards from "@/components/ActionCards";
import Simulator from "@/components/Simulator";
import AiInsight from "@/components/AiInsight";
import { useIsMobile } from "@/hooks/use-mobile";
import MobileHome from "@/mobile/MobileHome";

function fmt(amount) {
  return new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR" }).format(amount || 0);
}

function MetricCard({ title, value, subtitle, icon: Icon, color, link }) {
  const colors = {
    blue: "bg-blue-50 text-blue-600",
    green: "bg-emerald-50 text-emerald-600",
    red: "bg-rose-50 text-rose-600",
    amber: "bg-amber-50 text-amber-600",
    slate: "bg-slate-100 text-slate-600",
    purple: "bg-purple-50 text-purple-600",
  };
  const content = (
    <Card className="hover:shadow-md transition-shadow">
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs text-slate-500 uppercase tracking-wider font-medium mb-1">{title}</p>
            <p className="font-mono text-xl font-semibold text-slate-900">{value}</p>
            {subtitle && <p className="text-xs text-slate-400 mt-1">{subtitle}</p>}
          </div>
          <div className={`p-2 rounded-lg ${colors[color]}`}>
            <Icon className="w-4 h-4" strokeWidth={1.5} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
  return link ? <Link to={link} data-testid={`metric-${title.replace(/\s/g, "-").toLowerCase()}`}>{content}</Link> : <div data-testid={`metric-${title.replace(/\s/g, "-").toLowerCase()}`}>{content}</div>;
}

function ActionCard({ action }) {
  const colors = {
    urgent: "border-l-red-500 bg-red-50/50",
    warning: "border-l-amber-500 bg-amber-50/50",
    info: "border-l-blue-500 bg-blue-50/50",
  };
  const icons = { urgent: AlertTriangle, warning: AlertCircle, info: Bell };
  const Icon = icons[action.type] || AlertCircle;
  return (
    <div className={`p-3 rounded-lg border-l-4 ${colors[action.type]} flex items-center justify-between`} data-testid={`action-${action.type}`}>
      <div className="flex items-center gap-3">
        <Icon className="w-5 h-5 text-slate-600" strokeWidth={1.5} />
        <div>
          <p className="text-sm font-medium text-slate-800">{action.title}</p>
          <p className="text-xs text-slate-500">{action.message}</p>
        </div>
      </div>
      <ChevronRight className="w-4 h-4 text-slate-400" />
    </div>
  );
}

function DashboardDesktop() {
  const [kpis, setKpis] = useState(null);
  const [forecast, setForecast] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unreadAlerts, setUnreadAlerts] = useState(0);
  const [riskScore, setRiskScore] = useState(null);
  const [tensions, setTensions] = useState([]);
  const [cfoActions, setCfoActions] = useState([]);
  const [funnel, setFunnel] = useState(null);
  const [unassigned, setUnassigned] = useState({ total_count: 0, total_amount_eur: 0, unknown_eur_count: 0 });
  const [ivaToReserve, setIvaToReserve] = useState(null);
  const [irpfToReserve, setIrpfToReserve] = useState(0);
  // Campañas: módulo interno NEXO (decisión §5 2026-07-02); los tenants de producto no lo ven.
  // has_profile true = usuario de producto; false (o error) = tenant legacy NEXO.
  const [isLegacy, setIsLegacy] = useState(false);

  useEffect(() => {
    getCompanyProfile()
      .then(p => setIsLegacy(!p?.has_profile))
      .catch(() => setIsLegacy(true));
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [kpiRes, forecastRes, tasksRes, alertsRes, riskRes, tensionsRes, actionsRes, funnelRes, unassignedRes, fiscalRes] = await Promise.all([
          apiFetch(`${API}/dashboard/kpis`),
          apiFetch(`${API}/dashboard/forecast?months=3`),
          apiFetch(`${API}/dashboard/tasks`),
          apiFetch(`${API}/alerts/notifications/unread-count`),
          apiFetch(`${API}/cfo/risk-score`),
          apiFetch(`${API}/cfo/tensions`),
          apiFetch(`${API}/cfo/actions`),
          apiFetch(`${API}/cfo/kpi/funnel-conversion`),
          apiFetch(`${API}/crm/expenses/unassigned`),
          apiFetch(`${API}/fiscal/summary`),
        ]);
        if (kpiRes.ok) setKpis(await kpiRes.json());
        if (forecastRes.ok) setForecast(await forecastRes.json());
        if (tasksRes.ok) { const d = await tasksRes.json(); setTasks(d.tasks || []); }
        if (alertsRes.ok) { const d = await alertsRes.json(); setUnreadAlerts(d.count || 0); }
        if (riskRes.ok) setRiskScore(await riskRes.json());
        if (tensionsRes.ok) { const d = await tensionsRes.json(); setTensions(d.tensions || []); }
        if (actionsRes.ok) { const d = await actionsRes.json(); setCfoActions(d.actions || []); }
        if (funnelRes.ok) setFunnel(await funnelRes.json());
        if (unassignedRes.ok) {
          const data = await unassignedRes.json();
          setUnassigned({
            total_count: data.total_count || 0,
            total_amount_eur: data.total_amount_eur || 0,
            unknown_eur_count: data.unknown_eur_count || 0,
          });
        }
        if (fiscalRes.ok) {
          const d = await fiscalRes.json();
          setIvaToReserve(d.iva_to_reserve ?? 0);
          setIrpfToReserve(d.irpf_to_reserve ?? 0);
        }
      } catch (e) { console.error(e); } finally { setLoading(false); }
    };
    fetchData();
  }, []);

  const pct = (v) => `${((v || 0) * 100).toFixed(1)}%`;

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="spinner" /></div>;
  }

  const rev = kpis?.revenue || {};
  const exp = kpis?.expenses || {};
  const prof = kpis?.profitability || {};
  const cash = kpis?.cash || {};
  const subs = kpis?.subscriptions || {};
  const clients = kpis?.clients || {};
  const actions = kpis?.actions || [];
  const evolution = kpis?.monthly_evolution || [];
  const onboarding = kpis?.onboarding || {};
  // A7: sin movimientos del banco = modo arranque (checklist en vez de "crisis")
  const isStarting = (onboarding.bank_movements_count ?? 1) === 0;

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <PayrollCompleteness value={kpis?.payroll_completeness} />
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl lg:text-3xl font-bold text-slate-900 tracking-tight">Director Financiero</h1>
          <p className="text-sm text-slate-500 mt-1">
            Panel de control - {kpis?.period || new Date().toISOString().slice(0, 7)}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {unassigned.total_count > 0 && (
            <Link
              to="/facturas/recibidas"
              data-testid="unassigned-badge"
              aria-label={`${unassigned.total_count} gastos sin asignar por ${fmt(unassigned.total_amount_eur)}. Ir a facturas recibidas.`}
            >
              <Button variant="outline" size="sm" className="border-amber-300 text-amber-700 hover:bg-amber-50">
                <AlertTriangle className="w-4 h-4 mr-1" />
                {unassigned.total_count} sin asignar · {fmt(unassigned.total_amount_eur)}
              </Button>
            </Link>
          )}
          {(kpis?.expenses?.pending_bank_reconciliation_count ?? 0) > 0 && (
            <Link
              to="/facturas/recibidas"
              data-testid="pending-reconciliation-badge"
              aria-label={`${kpis.expenses.pending_bank_reconciliation_count} facturas pendientes de conciliación bancaria`}
            >
              <Button variant="outline" size="sm" className="border-sky-300 text-sky-700 hover:bg-sky-50">
                <AlertTriangle className="w-4 h-4 mr-1" />
                {kpis.expenses.pending_bank_reconciliation_count} pendiente{kpis.expenses.pending_bank_reconciliation_count !== 1 ? "s" : ""} de conciliación bancaria
              </Button>
            </Link>
          )}
          {unreadAlerts > 0 && (
            <Link to="/alertas">
              <Button variant="outline" size="sm" className="relative" data-testid="alerts-badge">
                <Bell className="w-4 h-4 mr-1" /> Alertas
                <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-red-500 text-white text-xs flex items-center justify-center">{unreadAlerts}</span>
              </Button>
            </Link>
          )}
        </div>
      </div>

      {/* A7: primeras 24 h — sin movimientos del banco, la app no grita
          "Runway 0,0 CRÍTICO": guía los primeros pasos. El análisis financiero
          se activa cuando hay datos reales que analizar. */}
      {isStarting && (
        <Card data-testid="starter-checklist" className="border-blue-200 bg-blue-50/40">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-heading font-semibold">
              Estás empezando — tres pasos para poner LiquidArc en marcha
            </CardTitle>
            <p className="text-xs text-slate-500 mt-1">
              El análisis financiero (riesgo, runway, tensiones) se activará en
              cuanto importes movimientos de tu banco.
            </p>
          </CardHeader>
          <CardContent className="space-y-2">
            <Link
              to="/banco/movimientos"
              className="flex items-center gap-3 bg-white border border-slate-200 rounded-lg px-4 py-3 hover:bg-slate-50 transition-colors"
              data-testid="starter-step-banco"
            >
              <Circle className="w-4 h-4 text-slate-300 shrink-0" />
              <Landmark className="w-5 h-5 text-slate-500 shrink-0" strokeWidth={1.5} />
              <div className="flex-1">
                <p className="text-sm font-medium text-slate-900">Importa los movimientos de tu banco</p>
                <p className="text-xs text-slate-500">La conciliación bancaria es la base de cobros y pagos.</p>
              </div>
              <ChevronRight className="w-4 h-4 text-slate-400" />
            </Link>
            <Link
              to="/facturas/emitidas"
              className="flex items-center gap-3 bg-white border border-slate-200 rounded-lg px-4 py-3 hover:bg-slate-50 transition-colors"
              data-testid="starter-step-factura"
            >
              {(onboarding.invoices_emitted_count ?? 0) > 0 ? (
                <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />
              ) : (
                <Circle className="w-4 h-4 text-slate-300 shrink-0" />
              )}
              <FileText className="w-5 h-5 text-slate-500 shrink-0" strokeWidth={1.5} />
              <div className="flex-1">
                <p className="text-sm font-medium text-slate-900">Crea tu primera factura</p>
                <p className="text-xs text-slate-500">Con tus datos de emisor y tu fiscalidad ya configuradas.</p>
              </div>
              <ChevronRight className="w-4 h-4 text-slate-400" />
            </Link>
            <Link
              to="/ajustes"
              className="flex items-center gap-3 bg-white border border-slate-200 rounded-lg px-4 py-3 hover:bg-slate-50 transition-colors"
              data-testid="starter-step-ajustes"
            >
              <Circle className="w-4 h-4 text-slate-300 shrink-0" />
              <SettingsIcon className="w-5 h-5 text-slate-500 shrink-0" strokeWidth={1.5} />
              <div className="flex-1">
                <p className="text-sm font-medium text-slate-900">Completa tus Ajustes</p>
                <p className="text-xs text-slate-500">Logo, series de facturación, IBAN y tu situación fiscal.</p>
              </div>
              <ChevronRight className="w-4 h-4 text-slate-400" />
            </Link>
          </CardContent>
        </Card>
      )}

      {/* Risk Score */}
      {!isStarting && <RiskGauge data={riskScore} />}

      {/* AI Insight */}
      {!isStarting && <AiInsight />}

      {/* Main KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <MetricCard title="Ingresos" value={fmt(rev.total)} subtitle={`${fmt(rev.collected)} cobrado`} icon={ArrowUpRight} color="green" link="/facturas/emitidas" />
        <MetricCard title="Gastos" value={fmt(exp.total)} subtitle={`${fmt(exp.paid)} pagado en facturas${exp.payroll_cost ? ` · ${fmt(exp.payroll_cost)} de coste laboral` : ""}`} icon={ArrowDownRight} color="red" link="/facturas/recibidas" />
        <MetricCard title="Beneficio Neto" value={fmt(prof.net_profit)} subtitle={`Margen: ${prof.gross_margin_pct || 0}%`} icon={TrendingUp} color={prof.net_profit >= 0 ? "green" : "red"} />
        <MetricCard title="Saldo Banco" value={fmt(cash.bank_balance)} icon={Wallet} color="blue" link="/banco/movimientos" />
        <MetricCard title="Clientes Activos" value={clients.active_count || 0} subtitle={`${clients.active_services || 0} servicios`} icon={Users} color="purple" link="/clientes" />
        <MetricCard title="Coste Herramientas" value={fmt(subs.monthly_cost)} subtitle={`${subs.active_count || 0} activas`} icon={CreditCard} color="amber" link="/suscripciones" />
      </div>

      {/* Secondary Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <MetricCard title="Pendiente Cobro" value={fmt(rev.pending)} subtitle={`${rev.overdue_count || 0} vencidas`} icon={Clock} color={rev.overdue_count > 0 ? "red" : "amber"} link="/facturas/emitidas" />
        <MetricCard title="Costes Cliente" value={fmt(exp.client_costs)} icon={Target} color="slate" />
        <MetricCard title="Inversion Propia" value={fmt(exp.own_investment)} icon={PieChart} color="purple" />
        <MetricCard title="Ingreso/Cliente" value={fmt(prof.avg_revenue_per_client)} icon={BarChart3} color="blue" />
        <MetricCard
          title="IVA a apartar"
          value={fmt(ivaToReserve)}
          subtitle={
            ivaToReserve < 0
              ? "a tu favor: el IVA soportado y las rectificativas superan el repercutido pendiente"
              : "pendiente de presentar"
          }
          icon={Wallet}
          color={ivaToReserve > 0 ? "amber" : "green"}
        />
        {irpfToReserve !== 0 && (
          <MetricCard
            title="IRPF a apartar"
            value={fmt(irpfToReserve)}
            subtitle="retenciones a proveedores pendientes de ingresar (modelo 111)"
            icon={Wallet}
            color={irpfToReserve > 0 ? "amber" : "green"}
          />
        )}
      </div>

      {/* Tensions */}
      {!isStarting && tensions.length > 0 && (
        <div>
          <h2 className="text-lg font-heading font-semibold text-slate-900 mb-3">Tensiones Detectadas</h2>
          <TensionCards tensions={tensions} />
        </div>
      )}

      {/* Actions — CFO Intelligence */}
      {!isStarting && cfoActions.length > 0 && (
        <div>
          <h2 className="text-lg font-heading font-semibold text-slate-900 mb-3">Acciones Recomendadas</h2>
          <ActionCards actions={cfoActions} />
        </div>
      )}

      {/* KPI 5 — Funnel de Conversion */}
      {/* Campañas: módulo interno NEXO (decisión §5 2026-07-02); los tenants de producto no lo ven */}
      {isLegacy && funnel && (funnel.by_campaign?.length > 0 || funnel.by_service_type?.length > 0) && (
        <Card data-testid="funnel-kpi-widget">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-heading font-semibold flex items-center gap-2">
              <Target className="w-5 h-5 text-blue-500" /> Funnel de Conversion Comercial
            </CardTitle>
            <p className="text-xs text-slate-500 mt-1">Contactados → Respuestas → Reuniones. El nivel por tipo de servicio es el que dirige decisiones.</p>
          </CardHeader>
          <CardContent className="space-y-5">
            {/* Level 3 — service_type (destacado) */}
            {funnel.by_service_type?.length > 0 && (
              <div className="bg-blue-50/60 border border-blue-200 rounded-lg p-4">
                <p className="text-xs uppercase tracking-wider font-semibold text-blue-700 mb-2">Por tipo de servicio</p>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead className="text-slate-500 uppercase">
                      <tr>
                        <th className="py-1 text-left font-semibold">Tipo</th>
                        <th className="py-1 text-right font-semibold">Contactados</th>
                        <th className="py-1 text-right font-semibold">Respuestas</th>
                        <th className="py-1 text-right font-semibold">Reuniones</th>
                        <th className="py-1 text-right font-semibold">% Respuesta</th>
                        <th className="py-1 text-right font-semibold">% Reunion/Resp.</th>
                        <th className="py-1 text-right font-semibold">Campanas</th>
                      </tr>
                    </thead>
                    <tbody>
                      {funnel.by_service_type.map(t => (
                        <tr key={t.service_type} className="border-t border-blue-100" data-testid={`funnel-type-${t.service_type}`}>
                          <td className="py-1.5 font-medium text-slate-800">{t.service_type}</td>
                          <td className="py-1.5 font-mono text-right">{t.leads_contacted}</td>
                          <td className="py-1.5 font-mono text-right">{t.leads_responded}</td>
                          <td className="py-1.5 font-mono text-right">{t.meetings_booked}</td>
                          <td className="py-1.5 font-mono text-right text-blue-700 font-semibold">{pct(t.response_rate)}</td>
                          <td className="py-1.5 font-mono text-right text-blue-700 font-semibold">{pct(t.meeting_rate_from_responses)}</td>
                          <td className="py-1.5 font-mono text-right text-slate-500">{t.n_campaigns} ({t.n_services} svc)</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Level 2 — service */}
            {funnel.by_service?.length > 0 && (
              <div>
                <p className="text-xs uppercase tracking-wider font-semibold text-slate-600 mb-2">Por servicio</p>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead className="text-slate-500 uppercase border-b">
                      <tr>
                        <th className="py-1 text-left font-semibold">Servicio</th>
                        <th className="py-1 text-right font-semibold">Contactados</th>
                        <th className="py-1 text-right font-semibold">Respuestas</th>
                        <th className="py-1 text-right font-semibold">Reuniones</th>
                        <th className="py-1 text-right font-semibold">% Respuesta</th>
                        <th className="py-1 text-right font-semibold">% Reunion/Resp.</th>
                      </tr>
                    </thead>
                    <tbody>
                      {funnel.by_service.map(s => (
                        <tr key={s.service_id} className="border-b border-slate-50" data-testid={`funnel-service-${s.service_id}`}>
                          <td className="py-1.5 text-slate-700">{s.service_name || s.service_id}</td>
                          <td className="py-1.5 font-mono text-right">{s.leads_contacted}</td>
                          <td className="py-1.5 font-mono text-right">{s.leads_responded}</td>
                          <td className="py-1.5 font-mono text-right">{s.meetings_booked}</td>
                          <td className="py-1.5 font-mono text-right">{pct(s.response_rate)}</td>
                          <td className="py-1.5 font-mono text-right">{pct(s.meeting_rate_from_responses)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Level 1 — campaign */}
            {funnel.by_campaign?.length > 0 && (
              <details className="group">
                <summary className="text-xs uppercase tracking-wider font-semibold text-slate-600 cursor-pointer hover:text-slate-900">
                  Por campana ({funnel.by_campaign.length}) — click para expandir
                </summary>
                <div className="overflow-x-auto mt-2">
                  <table className="w-full text-xs">
                    <thead className="text-slate-500 uppercase border-b">
                      <tr>
                        <th className="py-1 text-left font-semibold">Campana</th>
                        <th className="py-1 text-left font-semibold">Servicio</th>
                        <th className="py-1 text-right font-semibold">Contact.</th>
                        <th className="py-1 text-right font-semibold">Resp.</th>
                        <th className="py-1 text-right font-semibold">Reun.</th>
                        <th className="py-1 text-right font-semibold">% Resp.</th>
                        <th className="py-1 text-right font-semibold">% Reun/Resp.</th>
                      </tr>
                    </thead>
                    <tbody>
                      {funnel.by_campaign.map(c => (
                        <tr key={c.campaign_id} className="border-b border-slate-50" data-testid={`funnel-campaign-${c.campaign_id}`}>
                          <td className="py-1.5 text-slate-700">{c.name}</td>
                          <td className="py-1.5 text-slate-500">{c.service_name || "—"}</td>
                          <td className="py-1.5 font-mono text-right">{c.leads_contacted}</td>
                          <td className="py-1.5 font-mono text-right">{c.leads_responded}</td>
                          <td className="py-1.5 font-mono text-right">{c.meetings_booked}</td>
                          <td className="py-1.5 font-mono text-right">{pct(c.response_rate)}</td>
                          <td className="py-1.5 font-mono text-right">{pct(c.meeting_rate_from_responses)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </details>
            )}
          </CardContent>
        </Card>
      )}

      {/* Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Monthly Evolution */}
        {evolution.length > 0 && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg font-heading font-semibold">Evolucion Mensual</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="text-slate-500 uppercase border-b">
                    <tr>
                      <th className="py-2 text-left font-semibold">Mes</th>
                      <th className="py-2 text-right font-semibold">Ingresos</th>
                      <th className="py-2 text-right font-semibold">Gastos</th>
                      <th className="py-2 text-right font-semibold">Resultado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {evolution.slice(-6).map((m, idx, arr) => {
                      const prev = idx > 0 ? arr[idx - 1] : null;
                      const revTrend = prev ? m.revenue - prev.revenue : 0;
                      const expTrend = prev ? m.expenses - prev.expenses : 0;
                      const profTrend = prev ? m.profit - prev.profit : 0;
                      return (
                        <tr key={m.month} className="border-b border-slate-50">
                          <td className="py-2 font-mono font-medium">{m.month}</td>
                          <td className="py-2 font-mono text-right text-emerald-600">
                            <span className="inline-flex items-center gap-1">
                              {fmt(m.revenue)}
                              {prev && (revTrend >= 0
                                ? <TrendingUp className="w-3 h-3 text-emerald-500" />
                                : <TrendingDown className="w-3 h-3 text-rose-500" />
                              )}
                            </span>
                          </td>
                          <td className="py-2 font-mono text-right text-rose-600">
                            <span className="inline-flex items-center gap-1">
                              {fmt(m.expenses)}
                              {prev && (expTrend <= 0
                                ? <TrendingDown className="w-3 h-3 text-emerald-500" />
                                : <TrendingUp className="w-3 h-3 text-rose-500" />
                              )}
                            </span>
                          </td>
                          <td className={`py-2 font-mono text-right font-medium ${m.profit >= 0 ? "text-emerald-700" : "text-rose-700"}`}>
                            <span className="inline-flex items-center gap-1">
                              {fmt(m.profit)}
                              {prev && (profTrend >= 0
                                ? <TrendingUp className="w-3 h-3 text-emerald-500" />
                                : <TrendingDown className="w-3 h-3 text-rose-500" />
                              )}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Treasury Forecast */}
        {!isStarting && forecast && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg font-heading font-semibold flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-blue-500" /> Forecast de Tesoreria
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="mb-3 grid grid-cols-2 gap-3">
                <div className="bg-slate-50 rounded p-2">
                  <p className="text-xs text-slate-500">Ingreso Medio/Mes</p>
                  <p className="font-mono font-medium text-sm">{fmt(forecast.avg_monthly_income)}</p>
                </div>
                <div className="bg-slate-50 rounded p-2">
                  <p className="text-xs text-slate-500">Gasto Medio/Mes</p>
                  <p className="font-mono font-medium text-sm">{fmt(forecast.avg_monthly_expense)}</p>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="text-slate-500 uppercase border-b">
                    <tr>
                      <th className="py-2 text-left font-semibold">Mes</th>
                      <th className="py-2 text-right font-semibold">Ingreso</th>
                      <th className="py-2 text-right font-semibold">Gasto</th>
                      <th className="py-2 text-right font-semibold">Saldo</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b border-slate-100 bg-blue-50/30">
                      <td className="py-2 font-mono font-medium">Actual</td>
                      <td className="py-2" />
                      <td className="py-2" />
                      <td className="py-2 font-mono text-right font-semibold">{fmt(forecast.current_balance)}</td>
                    </tr>
                    {forecast.forecast?.map(f => (
                      <tr key={f.month} className="border-b border-slate-50">
                        <td className="py-2 font-mono font-medium">{f.month}</td>
                        <td className="py-2 font-mono text-right text-emerald-600">{fmt(f.projected_income)}</td>
                        <td className="py-2 font-mono text-right text-rose-600">{fmt(f.projected_expense)}</td>
                        <td className={`py-2 font-mono text-right font-medium ${f.projected_balance >= 0 ? "text-blue-700" : "text-rose-700"}`}>{fmt(f.projected_balance)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Simulator */}
        {!isStarting && (
          <div className="lg:col-span-2">
            <h2 className="text-lg font-heading font-semibold text-slate-900 mb-3">Simulador de Escenarios</h2>
            <Simulator />
          </div>
        )}

        {/* Overdue Invoices */}
        {rev.overdue_invoices?.length > 0 && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg font-heading font-semibold flex items-center gap-2 text-rose-600">
                <AlertTriangle className="w-5 h-5" /> Facturas Vencidas
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {rev.overdue_invoices.map(inv => (
                  <div key={inv.invoice_id} className="flex items-center justify-between bg-rose-50/50 rounded p-3 border-l-2 border-rose-400">
                    <div>
                      <p className="text-sm font-medium text-slate-800">{inv.client_name}</p>
                      <p className="text-xs text-slate-500">Vencio el {inv.due_date} ({inv.days_overdue} dias)</p>
                    </div>
                    <p className="font-mono font-semibold text-rose-600">{fmt(inv.pending)}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Upcoming Renewals */}
        {subs.upcoming_renewals?.length > 0 && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg font-heading font-semibold flex items-center gap-2">
                <RefreshCw className="w-5 h-5 text-blue-500" /> Renovaciones Proximas
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {subs.upcoming_renewals.map((r, i) => (
                  <div key={i} className="flex items-center justify-between bg-blue-50/50 rounded p-3">
                    <div>
                      <p className="text-sm font-medium text-slate-800">{r.name}</p>
                      <p className="text-xs text-slate-500">{r.renewal_date} (en {r.days_until} dias)</p>
                    </div>
                    <p className="font-mono font-medium">{r.price?.toLocaleString("es-ES", { minimumFractionDigits: 2 })} {r.currency}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Tasks */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-heading font-semibold">Tareas Pendientes</CardTitle>
          </CardHeader>
          <CardContent>
            {tasks.length === 0 ? (
              <div className="text-center py-6 text-slate-500">
                <CheckCircle2 className="w-10 h-10 mx-auto mb-2 text-emerald-500" />
                <p className="text-sm">Todo al dia</p>
              </div>
            ) : (
              <div className="space-y-2">
                {tasks.map((task, idx) => (
                  <Link key={idx} to={task.link} className={`flex items-center justify-between p-3 rounded-lg border-l-4 ${task.priority === "high" ? "border-l-rose-500 bg-rose-50/50" : task.priority === "medium" ? "border-l-amber-500 bg-amber-50/50" : "border-l-blue-500 bg-blue-50/50"} hover:shadow-sm transition-shadow`}>
                    <span className="text-sm font-medium text-slate-700">{task.title}</span>
                    <ChevronRight className="w-4 h-4 text-slate-400" />
                  </Link>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-lg font-heading font-semibold">Acceso Rapido</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <Link to="/banco/movimientos"><Button variant="outline" className="w-full justify-start text-xs" data-testid="quick-import-bank"><ArrowUpRight className="w-4 h-4 mr-2" />Importar Banco</Button></Link>
            <Link to="/facturas/recibidas"><Button variant="outline" className="w-full justify-start text-xs" data-testid="quick-upload-invoice"><ArrowDownRight className="w-4 h-4 mr-2" />Subir Factura</Button></Link>
            <Link to="/conciliacion"><Button variant="outline" className="w-full justify-start text-xs" data-testid="quick-reconcile"><Clock className="w-4 h-4 mr-2" />Conciliar</Button></Link>
            <Link to="/clientes"><Button variant="outline" className="w-full justify-start text-xs" data-testid="quick-clients"><Users className="w-4 h-4 mr-2" />Clientes</Button></Link>
            <Link to="/suscripciones"><Button variant="outline" className="w-full justify-start text-xs" data-testid="quick-subs"><CreditCard className="w-4 h-4 mr-2" />Suscripciones</Button></Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default function Dashboard() {
  const isMobile = useIsMobile();
  return isMobile ? <MobileHome /> : <DashboardDesktop />;
}
